// frontend/apps/widgets/src/widgets/GiftVoucher/utils/voucherSorting.js
/**
 * Vérifie si un voucher est featured (compatible avec les deux formats de données)
 * @param {Object} voucher - Voucher depuis l'API widget
 * @return {boolean} True si le voucher est featured
 */
export const isVoucherFeatured = (voucher) => {
  // Nouveau format (après mapper)
  if (voucher.display?.isHighlighted !== undefined) {
    return voucher.display.isHighlighted;
  }
  // Ancien format (compatibilité)
  return voucher.isHighlighted || false;
};

/**
 * Obtient le prix minimum d'un voucher (adapté pour l'API widget)
 * @param {Object} voucher - Voucher depuis l'API widget
 * @return {number} Prix minimum en centimes
 */
export const getMinVoucherPrice = (voucher) => {
  if (voucher.amount?.type === 'free') {
    return 0;
  }

  if (voucher.amount?.type === 'matrix' && voucher.amount?.pricingMatrix?.length > 0) {
    const availableEntries = voucher.amount.pricingMatrix.filter((e) => e.isAvailable !== false);
    return availableEntries.length > 0 ?
      Math.min(...availableEntries.map((e) => e.price || 0)) :
      Infinity;
  }

  // Pour les bons à prix fixe (nouveau format)
  if (voucher.amount?.value) {
    return voucher.amount.value;
  }

  // Ancien format (compatibilité)
  if (voucher.amountType === 'matrix' && voucher.variantConfig?.pricingMatrix?.length > 0) {
    const availableEntries = voucher.variantConfig.pricingMatrix.filter((e) => e.isAvailable !== false);
    return availableEntries.length > 0 ?
      Math.min(...availableEntries.map((e) => e.price || 0)) :
      Infinity;
  }

  return voucher.fixedAmount || 0;
};

/**
 * Obtient le prix maximum d'un voucher (pour tri décroissant)
 * @param {Object} voucher - Voucher depuis l'API widget
 * @return {number} Prix maximum en centimes
 */
export const getMaxVoucherPrice = (voucher) => {
  if (voucher.amount?.type === 'free') {
    return voucher.amount?.max || Infinity;
  }

  if (voucher.amount?.type === 'matrix' && voucher.amount?.pricingMatrix?.length > 0) {
    const availableEntries = voucher.amount.pricingMatrix.filter((e) => e.isAvailable !== false);
    return availableEntries.length > 0 ?
      Math.max(...availableEntries.map((e) => e.price || 0)) :
      0;
  }

  // Pour les bons à prix fixe (nouveau format)
  if (voucher.amount?.value) {
    return voucher.amount.value;
  }

  // Ancien format (compatibilité)
  if (voucher.amountType === 'matrix' && voucher.variantConfig?.pricingMatrix?.length > 0) {
    const availableEntries = voucher.variantConfig.pricingMatrix.filter((e) => e.isAvailable !== false);
    return availableEntries.length > 0 ?
      Math.max(...availableEntries.map((e) => e.price || 0)) :
      0;
  }

  return voucher.fixedAmount || 0;
};

/**
 * Calcule la fourchette de prix globale des vouchers
 * @param {Array} vouchers - Liste des vouchers
 * @return {Object} {min, max} en euros
 */
export const calculatePriceRange = (vouchers) => {
  if (!vouchers || vouchers.length === 0) {
    return {min: 0, max: 500};
  }

  let maxPrice = 0;

  // Exclure le type custom_voucher du calcul du prix range
  vouchers
      .filter((voucher) => voucher.type !== 'custom_voucher')
      .forEach((voucher) => {
        if (voucher.amount?.type === 'matrix' && voucher.amount?.pricingMatrix) {
          voucher.amount.pricingMatrix.forEach((entry) => {
            if (entry.price && entry.price > maxPrice) {
              maxPrice = entry.price;
            }
          });
        } else if (voucher.amount?.value && voucher.amount.value > maxPrice) {
          maxPrice = voucher.amount.value;
        } else if (voucher.amount?.max && voucher.amount.max > maxPrice) {
          maxPrice = voucher.amount.max;
        } else if (voucher.amountType === 'matrix' && voucher.variantConfig?.pricingMatrix) {
          voucher.variantConfig.pricingMatrix.forEach((entry) => {
            if (entry.price && entry.price > maxPrice) {
              maxPrice = entry.price;
            }
          });
        } else if (voucher.fixedAmount && voucher.fixedAmount > maxPrice) {
          maxPrice = voucher.fixedAmount;
        }
      });

  // Les prix arrivent déjà en euros depuis le backend, arrondir directement
  const roundedPrice = Math.ceil(maxPrice / 50) * 50;
  const finalMax = Math.max(roundedPrice, 100);

  return {
    min: 0,
    max: finalMax,
  };
};

/**
 * Fonction de tri interne (sans considération du featured)
 * @param {Array} vouchers - Liste des vouchers
 * @param {string} sortBy - Critère de tri
 * @return {Array} Vouchers triés
 */
const applySorting = (vouchers, sortBy = 'default') => {
  if (!vouchers || vouchers.length === 0) return [];

  return [...vouchers].sort((a, b) => {
    switch (sortBy) {
      case 'default':
        // Pour 'default', on garde l'ordre du backend qui est déjà trié selon catalogSortOrder
        // On retourne 0 pour conserver l'ordre original
        return 0;

      case 'price_asc': {
        // D'abord trier par type (value avant service) - compatible avec les deux formats
        const typeA = a.type || a.voucherType || 'value';
        const typeB = b.type || b.voucherType || 'value';

        if (typeA !== typeB) {
          return typeA === 'value' ? -1 : 1;
        }

        // Gérer les bons gratuits
        if ((a.amount?.type === 'free') || (a.amountType === 'free')) return -1;
        if ((b.amount?.type === 'free') || (b.amountType === 'free')) return 1;

        const priceA = getMinVoucherPrice(a);
        const priceB = getMinVoucherPrice(b);

        return priceA - priceB;
      }

      case 'price_desc': {
        const typeA = a.type || a.voucherType || 'value';
        const typeB = b.type || b.voucherType || 'value';

        if (typeA !== typeB) {
          return typeA === 'value' ? -1 : 1;
        }

        if ((a.amount?.type === 'free') || (a.amountType === 'free')) return -1;
        if ((b.amount?.type === 'free') || (b.amountType === 'free')) return 1;

        const priceA = getMaxVoucherPrice(a);
        const priceB = getMaxVoucherPrice(b);

        return priceB - priceA;
      }

      case 'alpha_asc':
        return a.name.localeCompare(b.name);

      case 'alpha_desc':
        return b.name.localeCompare(a.name);

      default:
        const typeA = a.type || a.voucherType || 'value';
        const typeB = b.type || b.voucherType || 'value';

        if (typeA !== typeB) {
          return typeA === 'value' ? -1 : 1;
        }
        return getMinVoucherPrice(a) - getMinVoucherPrice(b);
    }
  });
};

/**
 * Trie les vouchers selon le critère spécifié
 * En mode fixed/embedded : les featured sont toujours en premier
 * En mode floating : tri standard sans priorité aux featured
 * @param {Array} vouchers - Liste des vouchers
 * @param {string} sortBy - Critère de tri
 * @param {string} displayMode - Mode d'affichage ('fixed' ou 'floating')
 * @return {Array} Vouchers triés
 */
export const sortVouchers = (vouchers, sortBy = 'default', displayMode = 'fixed') => {
  if (!vouchers || vouchers.length === 0) return [];

  // En mode floating, pas de priorité aux featured - tri standard
  if (displayMode === 'floating') {
    if (sortBy === 'default') {
      return [...vouchers];
    }
    return applySorting(vouchers, sortBy);
  }

  // En mode fixed/embedded : séparer les vouchers featured des autres (ils restent en premier)
  const featuredVouchers = vouchers.filter((voucher) => isVoucherFeatured(voucher));
  const regularVouchers = vouchers.filter((voucher) => !isVoucherFeatured(voucher));

  // Pour 'default', on conserve l'ordre du backend dans chaque groupe
  if (sortBy === 'default') {
    return [...featuredVouchers, ...regularVouchers];
  }

  // Pour les autres tris, trier chaque groupe séparément
  const sortedFeatured = applySorting(featuredVouchers, sortBy);
  const sortedRegular = applySorting(regularVouchers, sortBy);

  // Retourner featured en premier, puis le reste
  return [...sortedFeatured, ...sortedRegular];
};

/**
 * Filtre les vouchers par fourchette de prix
 * @param {Array} vouchers - Liste des vouchers
 * @param {Array} priceRange - [min, max] en euros
 * @return {Array} Vouchers filtrés
 */
export const filterVouchersByPrice = (vouchers, priceRange) => {
  if (!vouchers || !priceRange) return vouchers;

  const [minPrice, maxPrice] = priceRange;

  return vouchers.filter((voucher) => {
    // Nouveau format
    if (voucher.amount?.type === 'free') {
      return true;
    }

    if (voucher.amount?.type === 'matrix' && voucher.amount?.pricingMatrix) {
      return voucher.amount.pricingMatrix.some((entry) => {
        return entry.price >= minPrice && entry.price <= maxPrice;
      });
    }

    // Pour les prix fixes (nouveau format)
    if (voucher.amount?.value) {
      return voucher.amount.value >= minPrice && voucher.amount.value <= maxPrice;
    }

    // Ancien format (compatibilité) - les prix arrivent déjà en euros depuis le backend
    if (voucher.amountType === 'free') {
      return true;
    }

    if (voucher.amountType === 'matrix' && voucher.variantConfig?.pricingMatrix) {
      return voucher.variantConfig.pricingMatrix.some((entry) => {
        return entry.price >= minPrice && entry.price <= maxPrice;
      });
    }

    // Pour les prix fixes (ancien format) - prix déjà en euros
    const price = voucher.fixedAmount || 0;
    return price >= minPrice && price <= maxPrice;
  });
};
